import React, { useEffect, useState } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ReactECharts from 'echarts-for-react';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from 'utils/access-token';
import { get } from '../../axios/apiService';
import { Grid, Card, CardContent, Typography, Avatar, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { fontSize } from '@mui/system';

function Dashboard() {
  const { instance } = useMsal();
  const [couponStats, setCouponStats] = useState([]);
  const [couponUsage, setCouponUsage] = useState([]);
  const [dashboardData, setDashboardData] = useState([]);

  const [trendData, setTrendData] = useState([]);
  const [trendInterval, setTrendInterval] = useState('Daily');
  const today = new Date();

  // Get date 7 days ago
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 6);
  const formatDate = (date) => date.toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(formatDate(sevenDaysAgo));
  const [endDate, setEndDate] = useState(formatDate(today));

  // Fetch redemption data from API
  const fetchCouponRedemptions = async () => {
    try {
      let response = await get('Coupon/redemptions'); // Uses GET helper like notify’s POST
      if (!response || response.length === 0) {
        console.warn('No coupon redemption data found.');
        return;
      }
      setCouponStats(response); // [{ couponName, redemptionCount }]
    } catch (error) {
      console.error('Failed to fetch coupon redemptions:', error);
    }
  };

  // fetch coupon usage transactions
  const fetchCouponUsageTransactions = async () => {
    try {
      let response = await get('Coupon/coupon-usage');
      if (!response || response.length === 0) {
        console.warn('No coupon usage data found.');
        return;
      }
      setCouponUsage(response);
    } catch (error) {
      console.error('Failed to fetch coupon usage:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      let response = await get('Customer/dashboard');

      setDashboardData(response);
    } catch (error) {
      console.error('Failed to fetch:', error);
    }
  };

  const fetchRedemptionTrends = async (interval = 'Daily', start, end) => {
    try {
      let url = `Coupon/redemption-trends?interval=${interval}`;
      if (start) url += `&startDate=${encodeURIComponent(start)}`;
      if (end) url += `&endDate=${encodeURIComponent(end)}`;

      let response = await get(url);
      if (!response || response.length === 0) {
        console.warn('No trend data found.');
        setTrendData([]);
        return;
      }
      setTrendData(response);
    } catch (error) {
      console.error('Failed to fetch redemption trends:', error);
      setTrendData([]);
    }
  };

  useEffect(() => {
    instance.initialize().then(() => {
      GetAccessToken(instance).then(() => {
        fetchCouponRedemptions();
        fetchRedemptionTrends();
        fetchCouponUsageTransactions();
        fetchDashboardData();
      });
    });
  }, [instance]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchRedemptionTrends(trendInterval, startDate, endDate);
    } else {
      setTrendData([]); // Clear data if date range incomplete
    }
  }, [instance, trendInterval, startDate, endDate]);

  // Convert API data to arrays
  const coupons = couponStats.map((c) => c.couponName);
  const redemptionData = couponStats.map((c) => c.redemptionCount);

  const couponCodes = couponUsage.map((u) => u.couponCode); // Fallback to 0 if undefined
  const revenueData = couponUsage.map((u) => u.totalRevenue);
  // Chart 1: Total Redemptions per Coupon (real data)
  const optionRedemptions = {
    //title: { text: 'Total Redemptions per Coupon', left: 'center' },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    xAxis: {
      type: 'value',
      name: 'Redemptions',
      nameLocation: 'middle',
      nameGap: 25,
      minInterval: 1
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '30', // make enough space for labels + axis name
      containLabel: true
    },
    yAxis: {
      type: 'category',
      data: coupons,
      name: 'Coupons'
    },
    series: [
      {
        name: 'Redemptions',
        type: 'bar',
        data: redemptionData,
        itemStyle: { color: '#3f51b5' }
      }
    ]
  };

  // Chart 2: Total Sales per Coupon (real data)
  const optionSales = {
    //title: { text: 'Coupon-wise Revenue', left: 'center' },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        let content = '';
        params.forEach((item) => {
          content += `${item.marker}${item.seriesName}: ₹${item.value}<br/>`;
        });
        return content;
      }
    },
    xAxis: {
      type: 'category',
      data: couponCodes,
      name: 'Coupons',
      nameLocation: 'middle',
      nameGap: 25,
      axisLabel: {
        fontSize: 10, // 🔹 change label size
        color: '#333' // optional: change color
      }
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '30', // make enough space for labels + axis name
      containLabel: true
    },
    yAxis: {
      type: 'value',
      name: 'Revenue(₹)',
      axisLabel: {
        formatter: function (value) {
          return '₹' + value; // Format as currency
        }
      }
    },
    series: [
      {
        name: 'Revenue',
        type: 'bar',
        data: revenueData.map((v) => v || 0), // Fallback to 0 if undefined
        itemStyle: { color: '#FFC107' },
        barWidth: '50%'
      }
    ]
  };

  // const optionRevenue = {
  //   title: { text: 'Coupon-wise Revenue Impact', left: 'center' },
  //   tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
  //   xAxis: { type: 'category', data: coupons },
  //   yAxis: { type: 'value', name: 'Revenue ($)' },
  //   series: [{
  //     name: 'Revenue',
  //     type: 'bar',
  //     data: redemptionData.map(v => v * 50), // Example derived metric
  //     itemStyle: { color: '#FFC107' }
  //   }]
  // };

  const trendCoupons = [...new Set(trendData.map((d) => d.couponCode))];
  const trendPeriods = [...new Set(trendData.map((d) => d.period))].sort();
  const series = trendCoupons.map((code) => ({
    name: code,
    type: 'line',
    data: trendPeriods.map((period) => {
      const found = trendData.find((d) => d.couponCode === code && d.period === period);
      return found ? found.redemptionCount : 0;
    }),
    symbol: 'circle'
  }));

  const optionTrends = {
    // title: { text: 'Redemption Trends', left: 'center' },
    tooltip: { trigger: 'axis' },
    legend: { data: trendCoupons, top: 10, left: 'center' },
    xAxis: {
      type: 'category',
      data: trendPeriods,
      name: trendInterval,
      nameLocation: 'middle',
      nameGap: 40
    },
    yAxis: {
      type: 'value',
      name: 'Redemptions',
      nameLocation: 'middle', // 'start' | 'middle' | 'end'
      nameRotate: 90, // rotate vertically
      nameGap: 30, // distance from axis
      nameTextStyle: {
        align: 'center', // align text relative to rotation
        verticalAlign: 'middle'
      },
      minInterval: 1,
      axisLabel: { formatter: (v) => Math.round(v) }
    },
    series
  };

  const optionFunnel = {
    //title: { text: 'Top-Performing Coupons', left: 'center' },
    tooltip: { trigger: 'item', formatter: '{b}' },
    series: [
      {
        name: 'Top Coupons',
        type: 'funnel',
        sort: 'descending',
        data: couponStats
          .map((c) => ({ name: c.couponName + ' - ' + c.redemptionCount, value: c.sequenceNo }))
          .sort((a, b) => b.value - a.value)
      }
    ]
  };

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      {/* Static Dashboard Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Avatar sx={{ backgroundColor: '#3f51b5' }}>
                    <PersonIcon sx={{ color: 'white' }} />
                  </Avatar>
                </Grid>
                <Grid item>
                  <Typography variant="h6">Total Users</Typography>
                  <Typography variant="subtitle1" gutterBottom>

                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Avatar sx={{ backgroundColor: '#4caf50' }}>
                    <AdminPanelSettingsIcon sx={{ color: 'white' }} />
                  </Avatar>
                </Grid>
                <Grid item>
                  <Typography variant="h6">Total Active Users</Typography>
                  <Typography variant="subtitle1" gutterBottom>

                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                   <Avatar sx={{ backgroundColor: '#f44336' }}>
                    <VerifiedUserIcon sx={{ color: 'white' }} />
                  </Avatar>
                </Grid>
                <Grid item>
                  <Typography variant="h6">Total Active Coupons</Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    
                  </Typography> 
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

     
    </div>
  );
}

export default Dashboard;
